import {
  Badge,
  Box,
  Flex,
  HStack,
  Icon,
  Progress,
  SimpleGrid,
  Text,
  Tooltip,
} from '@chakra-ui/react';
import i18n from 'i18next';
import { ArrowDown, ArrowUp, Coins, HardDrive, Languages, MessageSquare } from 'lucide-react';
import React, { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

interface CurrentUsageTabProps {
  usageData: any;
  limitation?: {
    storage_gb: number | null;
    question: number | null;
    translation: number | null;
    expiration: string | null;
  };
  /**
   * Show the Token usage card. Hidden on the admin's own "Usage & members" screen (token
   * data isn't surfaced there); kept on the super-admin Organizations detail. (#token-usage-card)
   */
  showTokenUsage?: boolean;
}

// Compact numeric formatter (1234 -> 1,234 ; 18400000 -> 18.4M)
const formatCompact = (n: number): string => {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1).replace(/\.0$/, '')}M`;
  if (n >= 10_000) return `${(n / 1_000).toFixed(1).replace(/\.0$/, '')}K`;
  return n.toLocaleString();
};

const CurrentUsageTab: React.FC<CurrentUsageTabProps> = ({
  usageData,
  limitation,
  showTokenUsage = true,
}) => {
  const { t } = useTranslation();

  // Card chrome shared by all KPI cards — mockup `.usage-card`
  const cardChrome = {
    p: 4,
    bg: 'bg.surface',
    borderWidth: '1px',
    borderColor: 'border.default',
    borderRadius: 'lg',
    boxShadow: 'ds_sm',
    display: 'flex',
    flexDirection: 'column' as const,
    minH: '148px',
  };

  const resetDate = limitation?.expiration
    ? new Date(limitation.expiration).toLocaleDateString(i18n.language, {
        month: 'short',
        day: 'numeric',
      })
    : null;

  // Meter KPI card — icon+label, mono used/limit, GREEN progress, caption
  const MeterCard = ({
    icon,
    label,
    used,
    limit,
    pct,
    isUnlimited,
    tooltip,
  }: {
    icon: ReactNode;
    label: string;
    used: ReactNode;
    limit: ReactNode;
    pct: number;
    isUnlimited: boolean;
    tooltip?: string;
  }) => (
    <Tooltip isDisabled={!tooltip} label={tooltip} placement='top'>
      <Box {...cardChrome}>
        <HStack mb={3} spacing={3}>
          <Flex
            align='center'
            justify='center'
            flexShrink={0}
            w='36px'
            h='36px'
            color='primary.solid'
            bg='primary.soft'
            borderRadius='md'
          >
            {icon}
          </Flex>
          <Text color='text.muted' fontSize='sm' fontWeight={500} noOfLines={1}>
            {label}
          </Text>
        </HStack>

        <HStack align='baseline' mb={3} spacing={2}>
          <Text
            color='text.default'
            fontFamily='mono'
            fontSize='xl'
            fontWeight={700}
            lineHeight={1}
          >
            {used}
          </Text>
          {!isUnlimited && (
            <>
              <Text color='text.subtle' fontFamily='mono' fontSize='md'>
                /
              </Text>
              <Text color='text.subtle' fontFamily='mono' fontSize='md'>
                {limit}
              </Text>
            </>
          )}
        </HStack>

        {isUnlimited ? (
          <Box mt='auto'>
            <Badge variant='ds-success'>{t('managementSystem.unlimited')}</Badge>
          </Box>
        ) : (
          <Box mt='auto'>
            {/* Usage health → green filledTrack (state.success) to match mockup */}
            <Progress
              sx={{ '& > div[role="progressbar"]': { bg: 'state.success' } }}
              h='6px'
              mb={2}
              borderRadius='full'
              value={pct}
              variant='ds'
            />
            <Text color='text.muted' fontSize='xs'>
              {t('managementSystem.companyDetail.percentUsed', { pct })}
              {resetDate
                ? ` · ${t('managementSystem.companyDetail.resetsOn', { date: resetDate })}`
                : ''}
            </Text>
          </Box>
        )}
      </Box>
    </Tooltip>
  );

  const num = (v: any) => Number(v || 0);

  const storageRaw = num(usageData?.current_usage?.total_storage_gb);
  const questionsRaw = num(usageData?.current_usage?.total_questions);
  const translationsRaw = num(usageData?.current_usage?.total_translations);
  const tokensIn = num(usageData?.current_usage?.total_input_token);
  const tokensOut = num(usageData?.current_usage?.total_output_token);

  const pctOf = (used: number, limit?: number | null) =>
    limit ? Math.min(100, Math.round((used / limit) * 100)) : 0;

  // Storage fields (total_storage_gb / limitation.storage_gb) arrive from the API in GB.
  const storageLimitGB = typeof limitation?.storage_gb === 'number' ? limitation.storage_gb : null;
  const storageUsed = (Math.round(storageRaw * 100) / 100).toFixed(2).replace(/\.?0+$/, '') || '0';
  const fmtStorageLimit = (g: number) =>
    g % 1 === 0
      ? g.toString()
      : Number(g)
          .toFixed(2)
          .replace(/\.?0+$/, '');

  return (
    <SimpleGrid columns={{ base: 1, sm: 2, lg: showTokenUsage ? 4 : 3 }} spacing={4}>
      {/* Storage */}
      <MeterCard
        icon={<Icon as={HardDrive} boxSize='18px' />}
        label={t('managementSystem.companyDetail.storageUsage')}
        used={`${storageUsed} GB`}
        limit={storageLimitGB !== null ? `${fmtStorageLimit(storageLimitGB)} GB` : ''}
        tooltip={`${storageUsed} GB${storageLimitGB !== null ? ` / ${storageLimitGB} GB` : ''}`}
        isUnlimited={!limitation?.storage_gb}
        pct={pctOf(storageRaw, limitation?.storage_gb)}
      />

      {/* Questions this month */}
      <MeterCard
        icon={<Icon as={MessageSquare} boxSize='18px' />}
        label={t('managementSystem.companyDetail.questionUsage')}
        used={formatCompact(questionsRaw)}
        limit={limitation?.question ? formatCompact(limitation.question) : ''}
        tooltip={`${questionsRaw.toLocaleString('en-US')}${
          limitation?.question ? ` / ${limitation.question.toLocaleString('en-US')}` : ''
        }`}
        isUnlimited={!limitation?.question}
        pct={pctOf(questionsRaw, limitation?.question)}
      />

      {/* Translations this month */}
      <MeterCard
        icon={<Icon as={Languages} boxSize='18px' />}
        label={t('managementSystem.companyDetail.translationUsage')}
        used={formatCompact(translationsRaw)}
        limit={limitation?.translation ? formatCompact(limitation.translation) : ''}
        tooltip={`${translationsRaw.toLocaleString('en-US')}${
          limitation?.translation ? ` / ${limitation.translation.toLocaleString('en-US')}` : ''
        }`}
        isUnlimited={!limitation?.translation}
        pct={pctOf(translationsRaw, limitation?.translation)}
      />

      {/* Token usage — in/out split, no meter (matches mockup) */}
      {showTokenUsage && (
        <Tooltip
          label={`${(tokensIn + tokensOut).toLocaleString('en-US')} (In ${tokensIn.toLocaleString(
            'en-US'
          )} · Out ${tokensOut.toLocaleString('en-US')})`}
          placement='top'
        >
          <Box {...cardChrome}>
            <HStack mb={3} spacing={3}>
              <Flex
                align='center'
                justify='center'
                flexShrink={0}
                w='36px'
                h='36px'
                color='primary.solid'
                bg='primary.soft'
                borderRadius='md'
              >
                <Icon as={Coins} boxSize='18px' />
              </Flex>
              <Text color='text.muted' fontSize='sm' fontWeight={500} noOfLines={1}>
                {t('managementSystem.companyDetail.tokenUsage')}
              </Text>
            </HStack>

            <Text
              mb={3}
              color='text.default'
              fontFamily='mono'
              fontSize='xl'
              fontWeight={700}
              lineHeight={1}
            >
              {formatCompact(tokensIn + tokensOut)}
            </Text>

            <HStack justify='space-between' mt='auto'>
              <HStack color='text.muted' spacing={1}>
                <Icon as={ArrowUp} boxSize='14px' color='state.success' />
                <Text fontFamily='mono' fontSize='xs'>
                  {t('managementSystem.companyDetail.tokensIn', { value: formatCompact(tokensIn) })}
                </Text>
              </HStack>
              <HStack color='text.muted' spacing={1}>
                <Icon as={ArrowDown} boxSize='14px' color='text.subtle' />
                <Text fontFamily='mono' fontSize='xs'>
                  {t('managementSystem.companyDetail.tokensOut', {
                    value: formatCompact(tokensOut),
                  })}
                </Text>
              </HStack>
            </HStack>
          </Box>
        </Tooltip>
      )}
    </SimpleGrid>
  );
};

export default CurrentUsageTab;
